'use client';

import React, { useState, useEffect } from 'react';
import { Card, CardBody, Button, addToast } from '@heroui/react';
import { Typography, CustomHeroButton, CustomHeroInput } from '../../../../../components';
import { InfoIcon } from '../../../../../components/icons';
import clientAccService from '../../../../../api/client/clientAccService';
import { handleApiError } from '../../../../../utils/errorHandler';
import LinkIcon from './link-icon.svg';
import { showCreatorSuccess, showError } from '../../../../../utils/toast';
import { useTranslations } from '../../../../../contexts/translation.context';

interface WorkContentTabProps {
  modelId?: string;
  initialData?: {
    nonude?: string;
    tt?: string;
    lingerie?: string;
    lite_nude?: string;
    full_nude?: string;
  };
}

interface WorkContentData {
  nonude: string;
  tt: string;
  lingerie: string;
  lite_nude: string;
  full_nude: string;
}

const WorkContentTab: React.FC<WorkContentTabProps> = ({ modelId, initialData = {} }) => {
  const { t, lang } = useTranslations();
  const [formData, setFormData] = useState<WorkContentData>({
    nonude: initialData?.nonude || '',
    tt: initialData?.tt || '',
    lingerie: initialData?.lingerie || '',
    lite_nude: initialData?.lite_nude || '',
    full_nude: initialData?.full_nude || '',
  });

  const [isSaving, setIsSaving] = useState(false);

  // Обновляем formData при изменении initialData
  useEffect(() => {
    setFormData({
      nonude: initialData?.nonude || '',
      tt: initialData?.tt || '',
      lingerie: initialData?.lingerie || '',
      lite_nude: initialData?.lite_nude || '',
      full_nude: initialData?.full_nude || '',
    });
  }, [initialData]);

  const handleInputChange = (field: keyof WorkContentData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!modelId) {
      console.error('ModelId не найден для сохранения данных');
      return;
    }

    setIsSaving(true);
    try {
      const response = await clientAccService.workMedia(modelId, formData);
      console.log('Рабочий контент успешно сохранен:', response);

      // Показываем toast уведомление об успешном сохранении
      // addToast({
      //   title: "Успешно!",
      //   description: "Рабочий контент успешно сохранен",
      //   color: "success",
      //   timeout: 3000,
      // });
      showCreatorSuccess(t('accountPage.workContent.success.saved'));
    } catch (error: any) {
      console.error('Ошибка при сохранении рабочего контента:', error);

      // Показываем toast уведомление об ошибке
      const errorMessage = handleApiError(error);
      // addToast({
      //   title: "Ошибка!",
      //   description: errorMessage,
      //   color: "danger",
      //   timeout: 5000,
      // });
      showError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const contentFields = [
    {
      key: 'nonude' as keyof WorkContentData,
      label: 'No nude/Lifestyle',
      placeholder: 'https://drive.google.com/drive/folders/...',
      tooltipKey: 'accountPage.workContent.fields.nonudeTooltip',
    },
    {
      key: 'tt' as keyof WorkContentData,
      label: 'TikTok',
      placeholder: '@princess.ri',
      tooltipKey: 'accountPage.workContent.fields.ttTooltip',
    },
    {
      key: 'lingerie' as keyof WorkContentData,
      label: 'Lingerie',
      placeholder: 'https://drive.google.com/drive/folders/...',
      tooltipKey: 'accountPage.workContent.fields.lingerieTooltip',
    },
    {
      key: 'lite_nude' as keyof WorkContentData,
      label: 'Light Nudes',
      placeholder: 'https://drive.google.com/drive/folders/...',
      tooltipKey: 'accountPage.workContent.fields.liteNudeTooltip',
    },
    {
      key: 'full_nude' as keyof WorkContentData,
      label: 'Full Nude',
      placeholder: 'https://drive.google.com/drive/folders/...',
      tooltipKey: 'accountPage.workContent.fields.fullNudeTooltip',
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Typography variant="h6" className="">
        {t('accountPage.workContent.title')}
      </Typography>

      {/* Information Block */}
      <Card className="border border-l-[0.1875rem] border-l-partner rounded-none shadow-sm bg-gray-98">
        <CardBody className="p-4">
          <div className="flex gap-4">
            <div className="icon size-8 md:size-9">
              <InfoIcon />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <Typography variant="body2" className="!text-partner font-medium leading-5">
                  {t('accountPage.workContent.info.title')}
                </Typography>
                <Typography variant="body2" className="text-auth-muted leading-5">
                  {t('accountPage.workContent.info.description')}
                </Typography>
              </div>

              {/* Technical Specification Link */}
              <a
                href={
                  lang === 'ru'
                    ? 'https://telegra.ph/Kontent-TZ-dlya-modelej-10-30'
                    : 'https://telegra.ph/Terms-of-Reference-for-Content-Models--OnlyU-10-30'
                }
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-husky rounded-5 px-2 py-0.5 w-fit"
              >
                <img src={LinkIcon} className="size-[1.125rem]" alt="Link" />
                <Typography variant="body2" className="!text-partner leading-5">
                  {t('accountPage.workContent.info.technicalSpec')}
                </Typography>
              </a>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Content Form */}
      <div className="w-full max-w-[40.0625rem]">
        <div className="flex flex-col gap-4">
          {contentFields.map((field) => (
            <CustomHeroInput
              key={field.key}
              label={field.label}
              infoIconText={t(field.tooltipKey)}
              name={field.key}
              value={formData[field.key]}
              onChange={(e) => handleInputChange(field.key, e.target.value)}
              placeholder={field.placeholder}
            />
          ))}
        </div>

        {/* Save Button */}
        <CustomHeroButton
          className="mt-6  text-white rounded-xl h-10 px-6"
          onPress={handleSave}
          isLoading={isSaving}
          disabled={isSaving}
          title={
            isSaving
              ? t('accountPage.workContent.buttons.saving')
              : t('accountPage.workContent.buttons.save')
          }
        />
      </div>
    </div>
  );
};

export default WorkContentTab;
